import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

interface Payment {
  id: string;
  username: string;
  status: string;
  amount_usd: number;
  expected_amount_usdt: number;
  generated_address: string;
  created_at: string;
  expires_at: string;
  transaction_hash?: string;
  admin_confirmed_by?: string;
  created_at_moscow?: string;
  expires_at_moscow?: string;
}

export function PaymentsManagement() {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/auth/admin/payments', {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          setPayments(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async (paymentId: string) => {
    if (!confirm('Вы уверены, что хотите подтвердить этот платеж?')) return;

    try {
      const response = await fetch(`/api/auth/admin/payments/${paymentId}/confirm`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.ok) {
          fetchPayments();
        }
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (!confirm('Вы уверены, что хотите удалить этот платеж?')) return;

    try {
      const response = await fetch(`/api/auth/admin/payments/${paymentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        fetchPayments();
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
      case 'expired':
        return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
      case 'cancelled':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Завершен';
      case 'pending': return 'Ожидает';
      case 'expired': return 'Истек';
      case 'cancelled': return 'Отменен';
      default: return status;
    }
  };

  const handleViewDetails = (payment: Payment) => {
    setSelectedPayment(payment);
    setIsDetailsDialogOpen(true);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Управление платежами</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Загрузка платежей...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Управление платежами</h2>
            <p className="text-muted-foreground">Просмотр, подтверждение и удаление платежей в системе</p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/admin">← Назад к обзору</Link>
          </Button>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Платежи системы</CardTitle>
        <CardDescription>
          Просмотр и управление платежами пользователей
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {payments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Нет платежей
            </div>
          ) : (
            payments.map((payment) => (
              <div key={payment.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">{payment.username}</span>
                    <span className="text-sm text-muted-foreground">
                      ${payment.amount_usd}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${getStatusColor(payment.status)}`}>
                      {getStatusText(payment.status)}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    ID: {payment.id.substring(0, 8)}...
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Создан: {payment.created_at_moscow || new Date(payment.created_at).toLocaleString()}
                  </div>
                  {payment.expires_at && (
                    <div className="text-sm text-muted-foreground">
                      Истекает: {payment.expires_at_moscow || new Date(payment.expires_at).toLocaleString()}
                    </div>
                  )}
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDetails(payment)}
                  >
                    Подробнее
                  </Button>
                  {payment.status === 'pending' && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleConfirmPayment(payment.id)}
                    >
                      Подтвердить
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeletePayment(payment.id)}
                  >
                    Удалить
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Payment Details Dialog */}
        <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Детали платежа</DialogTitle>
              <DialogDescription>
                Подробная информация о платеже {selectedPayment?.id}
              </DialogDescription>
            </DialogHeader>
            {selectedPayment && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">ID платежа</Label>
                    <p className="text-sm text-muted-foreground">{selectedPayment.id}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Пользователь</Label>
                    <p className="text-sm text-muted-foreground">{selectedPayment.username}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Сумма USD</Label>
                    <p className="text-sm text-muted-foreground">${selectedPayment.amount_usd}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Ожидается USDT</Label>
                    <p className="text-sm text-muted-foreground">{selectedPayment.expected_amount_usdt}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Статус</Label>
                    <span className={`text-xs px-2 py-1 rounded ${getStatusColor(selectedPayment.status)}`}>
                      {getStatusText(selectedPayment.status)}
                    </span>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Адрес кошелька</Label>
                    <p className="text-sm text-muted-foreground font-mono break-all">
                      {selectedPayment.generated_address}
                    </p>
                  </div>
                </div>
                
                {selectedPayment.transaction_hash && (
                  <div>
                    <Label className="text-sm font-medium">Хеш транзакции</Label>
                    <p className="text-sm text-muted-foreground font-mono break-all">
                      {selectedPayment.transaction_hash}
                    </p>
                  </div>
                )}
                
                {selectedPayment.admin_confirmed_by && (
                  <div>
                    <Label className="text-sm font-medium">Подтвержден администратором</Label>
                    <p className="text-sm text-muted-foreground">{selectedPayment.admin_confirmed_by}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
